import { useState } from 'react';

import { api } from '../api/client';
import type { RoleRow } from '../api/types';
import { Badge, Button, Card, Field, Form, Loading, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';
import { article } from '../vocabulary';

/**
 * Visit rules — how much work a person is given.
 *
 * This screen replaced one that listed `policy_config` keys: `validation.sampling_rate`,
 * `scheduling.freeze_after_days`. Those are engineering settings, and putting them on an
 * administrator's first screen asks them to learn our vocabulary before they can answer
 * their own question, which is simply *how many places should this person visit in a day?*
 *
 * The engineering settings still exist, lower down, for whoever actually needs them.
 */

const DAYS: Array<{ key: keyof VisitRule['workingDays']; label: string }> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

interface VisitRule {
  id: string;
  roleId: string;
  roleName: string;
  visitsPerDay: number;
  workingDays: {
    sunday: boolean;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
  };
  avoidRepeatVisits: boolean;
  repeatAfterDays: number;
  requireLocationAtCheckIn: boolean;
  requireSelfieAtCheckIn: boolean;
  allowAfterScheduledDay: boolean;
  freezeAtTime: string | null;
  visitsPerWeek: number;
}

interface PolicyRow {
  id: string;
  key: string;
  value: string;
  description: string | null;
}

export function VisitRules(): JSX.Element {
  const { can, config } = useSession();
  const rules = useResource(() => api.get<VisitRule[]>('/visit-rules'), []);
  const roles = useResource(() => api.get<RoleRow[]>('/roles'), []);
  const [editing, setEditing] = useState<VisitRule | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <Card
        title="Visit rules"
        // See Rules.tsx: the server refuses either way; this stops the screen offering it.
        actions={can('policy.edit') ? <Button onClick={() => setCreating(true)}>Add a rule</Button> : undefined}
      >
        <p className="muted">
          How many places {article(config.fieldUserLabel)} {config.fieldUserLabel.toLowerCase()}{' '}
          visits, and on which days.
          The schedule generator uses exactly these numbers.
        </p>

        {rules.loading ? (
          <Loading />
        ) : rules.error !== null ? (
          <Notice kind="error">{rules.error}</Notice>
        ) : (
          <Table
            rows={rules.data ?? []}
            keyOf={(row) => row.id}
            empty="No visit rules yet. Without one, a schedule cannot be generated."
            onRowClick={(row) => setEditing(row)}
            columns={[
              { header: 'Applies to', render: (row) => row.roleName },
              {
                header: `${config.siteLabel}s per day`,
                numeric: true,
                render: (row) => row.visitsPerDay,
              },
              {
                header: 'Working days',
                render: (row) => (
                  <div className="row">
                    {DAYS.map((day) => (
                      // "low" renders green, the same as "accepted", so an off day looked
                      // identical to a working one. Neutral grey is the whole point here.
                      <Badge key={day.key} tone={row.workingDays[day.key] ? 'accepted' : 'muted'}>
                        {day.label}
                      </Badge>
                    ))}
                  </div>
                ),
              },
              {
                header: 'Per week',
                numeric: true,
                // Derived from the two columns beside it, so it cannot disagree with them.
                render: (row) => row.visitsPerWeek,
              },
              {
                header: 'Return to a place',
                render: (row) =>
                  row.avoidRepeatVisits ? `Not within ${row.repeatAfterDays} days` : 'Any time',
              },
            ]}
          />
        )}
      </Card>

      {(editing !== null || creating) && (
        <RuleForm
          rule={editing}
          roles={roles.data ?? []}
          siteLabel={config.siteLabel}
          fieldUserLabel={config.fieldUserLabel}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            rules.reload();
          }}
        />
      )}

      <AdvancedSettings />
    </>
  );
}

function RuleForm({
  rule,
  roles,
  siteLabel,
  fieldUserLabel,
  onClose,
  onSaved,
}: {
  rule: VisitRule | null;
  roles: RoleRow[];
  siteLabel: string;
  fieldUserLabel: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [roleId, setRoleId] = useState(rule?.roleId ?? '');
  const [visitsPerDay, setVisitsPerDay] = useState(String(rule?.visitsPerDay ?? 2));
  const [days, setDays] = useState(
    rule?.workingDays ?? {
      sunday: false,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
    },
  );
  const [avoidRepeat, setAvoidRepeat] = useState(rule?.avoidRepeatVisits ?? true);
  const [repeatAfter, setRepeatAfter] = useState(String(rule?.repeatAfterDays ?? 30));
  const [requireLocation, setRequireLocation] = useState(rule?.requireLocationAtCheckIn ?? false);
  const [requireSelfie, setRequireSelfie] = useState(rule?.requireSelfieAtCheckIn ?? false);
  const [sameDayOnly, setSameDayOnly] = useState(rule?.allowAfterScheduledDay === false);
  const [freezeAt, setFreezeAt] = useState(rule?.freezeAtTime ?? '18:00');

  const workingCount = Object.values(days).filter(Boolean).length;
  const perWeek = workingCount * Math.max(0, Number(visitsPerDay) || 0);

  const submit = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.put('/visit-rules', {
        roleId,
        visitsPerDay: Number(visitsPerDay),
        workingDays: days,
        avoidRepeatVisits: avoidRepeat,
        repeatAfterDays: Number(repeatAfter),
        requireLocationAtCheckIn: requireLocation,
        requireSelfieAtCheckIn: requireSelfie,
        allowAfterScheduledDay: !sameDayOnly,
        freezeAtTime: sameDayOnly ? freezeAt : null,
      });
    });
    if (ok) onSaved();
  };

  return (
    <Card title={rule === null ? 'Add a visit rule' : `${rule.roleName} visit rule`}>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <Form onSubmit={() => void submit()}>
        <div className="form-row">
          <Select
            label="Applies to"
            value={roleId}
            onChange={setRoleId}
            required
            options={roles.map((role) => ({ value: role.id, label: role.name }))}
          />
          <Field
            label={`${siteLabel}s per working day`}
            value={visitsPerDay}
            onChange={setVisitsPerDay}
            type="number"
            required
          />
        </div>

        <div className="field">
          <span className="field-label">Working days</span>
          <div className="row">
            {DAYS.map((day) => (
              <label key={day.key} className="row">
                <input
                  type="checkbox"
                  checked={days[day.key]}
                  onChange={() => setDays({ ...days, [day.key]: !days[day.key] })}
                />
                <span>{day.label}</span>
              </label>
            ))}
          </div>
          {/* Shown while typing, because "3 per day" and "15 per week" are the same
              decision and an administrator is usually thinking in the second one. */}
          <span className="field-hint">
            That is <strong>{perWeek}</strong> {siteLabel.toLowerCase()} visit(s) per week.
          </span>
        </div>

        <div className="row">
          <label className="row">
            <input type="checkbox" checked={avoidRepeat} onChange={() => setAvoidRepeat(!avoidRepeat)} />
            <span>Avoid sending someone back to the same place too soon</span>
          </label>
        </div>

        {avoidRepeat && (
          <Field
            label="Days before a return visit"
            value={repeatAfter}
            onChange={setRepeatAfter}
            type="number"
            hint="Places visited more recently than this are skipped when generating."
          />
        )}

        <h3 style={{ marginTop: 16 }}>Starting a visit</h3>

        <div className="row">
          <label className="row">
            <input
              type="checkbox"
              checked={requireLocation}
              onChange={() => setRequireLocation(!requireLocation)}
            />
            <span>Must be at the {siteLabel.toLowerCase()} to start</span>
          </label>
        </div>

        {/* Said before it is switched on, because the failure mode is silent and total: a
            deployment whose coordinates are wrong finds every visit impossible. */}
        <p className="muted">
          {requireLocation
            ? `A check-in from outside the ${siteLabel.toLowerCase()}'s radius is refused. The attempt is still recorded. Check your coordinates are right before relying on this.`
            : `Distance is measured and flagged at review, but a visit can still be started from anywhere.`}
        </p>

        <div className="row">
          <label className="row">
            <input
              type="checkbox"
              checked={requireSelfie}
              onChange={() => setRequireSelfie(!requireSelfie)}
            />
            <span>Photograph of the {fieldUserLabel.toLowerCase()} needed to start</span>
          </label>
        </div>

        <p className="muted">
          Front camera, taken in the app. There is no option to choose an existing photograph.
          A visit record can say where the phone was; only this says who was holding it.
        </p>

        <h3 style={{ marginTop: 16 }}>Finishing a visit</h3>

        <div className="row">
          <label className="row">
            <input
              type="checkbox"
              checked={sameDayOnly}
              onChange={() => setSameDayOnly(!sameDayOnly)}
            />
            <span>Work must be recorded on the scheduled day</span>
          </label>
        </div>

        {sameDayOnly ? (
          <>
            <Field
              label="Close the day's visits at"
              type="time"
              value={freezeAt}
              onChange={setFreezeAt}
              hint="Local time. Leave it until the end of the working day."
            />
            {/* The two outcomes are different on purpose, and an administrator turning this
                on should know which is which before it happens to real visits. */}
            <p className="muted">
              At that time, a visit already under way is <strong>frozen</strong> with whatever
              was collected. One never started is marked <strong>not performed</strong> — a
              missed visit, not a half-finished one. A reviewer can re-open either with a
              reason; left alone, a missed visit stays missed.
            </p>
          </>
        ) : (
          <p className="muted">
            Visits stay open after their date and are closed by the general housekeeping
            sweep. Nothing runs on a timer for this {fieldUserLabel.toLowerCase()}.
          </p>
        )}

        <div className="form-actions">
          <Button type="submit" disabled={busy || roleId === '' || visitsPerDay === ''}>
            {busy ? 'Saving…' : 'Save rule'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </Card>
  );
}

/**
 * The raw settings, kept but demoted.
 *
 * Removing them would have been the easy simplification and the wrong one: they still
 * control real behaviour, and hiding a setting that affects a deployment is how an
 * operator ends up editing the database by hand.
 */
function AdvancedSettings(): JSX.Element {
  const policies = useResource(() => api.get<PolicyRow[]>('/policies'), []);
  const { run, error, busy } = useAction();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);

  const save = async (): Promise<void> => {
    if (editing === null) return;
    const ok = await run(async () => {
      await api.put(`/policies/${encodeURIComponent(editing.key)}`, { value: editing.value });
    });
    if (ok) {
      setEditing(null);
      policies.reload();
    }
  };

  return (
    <Card
      title="Advanced settings"
      actions={
        <Button variant="secondary" onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    >
      {!open ? (
        <p className="muted">
          Spot-check sampling and housekeeping timings. Most deployments never change these.
        </p>
      ) : policies.loading ? (
        <Loading />
      ) : (
        <>
          {error !== null && <Notice kind="error">{error}</Notice>}
          <Table
            rows={policies.data ?? []}
            keyOf={(row) => row.id}
            empty="Nothing set — built-in defaults apply."
            columns={[
              { header: 'Setting', render: (row) => <code>{row.key}</code> },
              { header: 'Value', render: (row) => row.value },
              {
                header: '',
                render: (row) => (
                  <Button
                    variant="secondary"
                    onClick={() => setEditing({ key: row.key, value: row.value })}
                  >
                    Edit
                  </Button>
                ),
              },
            ]}
          />

          {editing !== null && (
            <Form onSubmit={() => void save()}>
              <Field
                label={editing.key}
                value={editing.value}
                onChange={(value) => setEditing({ ...editing, value })}
                required
              />
              <div className="form-actions">
                <Button type="submit" disabled={busy}>
                  Save
                </Button>
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </>
      )}
    </Card>
  );
}
